import _ from 'underscore';
import { LocationTarget, compareLocationTargetArrays } from '@bingads-webui-react/bing-maps';

import { LocationTargetTypes as LOCATION_TARGET_TYPES } from './location-target-types';
import { ENTITY_ACTION_TYPES } from './entity-action-types';

const RADIUS_UNIT_MAP = {
  mi: 'mi',
  km: 'km',
  Miles: 'mi',
  Kilometers: 'km',
};

export const INTENT_TARGET_VALUE = {
  PeopleIn: 'PeopleIn',
  PeopleSearchingForOrViewingPages: 'PeopleSearchingForOrViewingPages',
  PeopleInOrSearchingForOrViewingPages: 'PeopleInOrSearchingForOrViewingPages',
};

export const fromLocationCriterionToBingMapTarget = data => (
  _.chain(data)
    .filter(criterion => criterion.Criterion.Type !== 'LocationIntentOption')
    .map((criterion) => {
      const locationTarget =
      criterion['@odata.type'] === '#Model.NegativeCampaignCriterion' ?
        LocationTarget.CreateExcludedLocationTarget({
          CountryCode: criterion.Criterion.CountryCode,
          ExpandedName: criterion.Criterion.Name,
          GeoPoint: criterion.Criterion.GeoPoint ? criterion.Criterion.GeoPoint : {
            Latitude: criterion.Criterion.Latitude,
            Longitude: criterion.Criterion.Longitude,
          },
          Id: criterion.Criterion.LocationId,
          Type: criterion.Criterion.LocationType || criterion.Criterion.Type,
        }, criterion.Criterion.Radius, RADIUS_UNIT_MAP[criterion.Criterion.RadiusUnit])
        : LocationTarget.CreateIncludedLocationTarget({
          CountryCode: criterion.Criterion.CountryCode,
          ExpandedName: criterion.Criterion.Name,
          GeoPoint: criterion.Criterion.GeoPoint ? criterion.Criterion.GeoPoint : {
            Latitude: criterion.Criterion.Latitude,
            Longitude: criterion.Criterion.Longitude,
          },
          Id: criterion.Criterion.LocationId,
          Type: criterion.Criterion.LocationType || criterion.Criterion.Type,
        }, criterion.Criterion.Radius, RADIUS_UNIT_MAP[criterion.Criterion.RadiusUnit]);
      locationTarget.Id = criterion.Id;
      return locationTarget;
    })
    .value()
);

export const getIntentCriterion = criterions => _.filter(criterions, criterion => criterion.Criterion.Type === 'LocationIntentOption')[0];

export const getIntentCriterionValues = (intentCriterion) => {
  let peopleInLocation = null;
  let peopleSearchingForLocation = null;

  switch (intentCriterion && intentCriterion.Criterion
    && intentCriterion.Criterion.IntentOption) {
    case INTENT_TARGET_VALUE.PeopleIn:
      peopleInLocation = true;
      peopleSearchingForLocation = false;
      break;
    case INTENT_TARGET_VALUE.PeopleSearchingForOrViewingPages:
      peopleSearchingForLocation = true;
      peopleInLocation = false;
      break;
    case INTENT_TARGET_VALUE.PeopleInOrSearchingForOrViewingPages:
    default:
      peopleInLocation = true;
      peopleSearchingForLocation = true;
      break;
  }

  return { peopleInLocation, peopleSearchingForLocation };
};

export const getIntentValue = ({
  peopleInLocation,
  peopleSearchingForLocation,
  peopleInOrSearchingForLocation,
}) => {
  let intent = '';

  if (peopleInOrSearchingForLocation) {
    intent = INTENT_TARGET_VALUE.PeopleInOrSearchingForOrViewingPages;
  } else if (peopleInLocation) {
    intent = INTENT_TARGET_VALUE.PeopleIn;
  } else if (peopleSearchingForLocation) {
    intent = INTENT_TARGET_VALUE.PeopleSearchingForOrViewingPages;
  }

  return intent;
};

export const getLocationsToSave = (initialTargets, targets) => {
  const diff = compareLocationTargetArrays(initialTargets, targets);
  const locationsToAdd = [];
  const locationsToDelete = [];

  _.each(diff.Deleted, (deletedTarget) => {
    locationsToDelete.push(deletedTarget);
  });

  _.each(diff.Added, (addedTarget) => {
    locationsToAdd.push(addedTarget);
  });

  // NOTE: Backend does not support updating an existing location target's radius
  // Updates will be processed as a delete and then an add
  _.each(diff.Updated, (updatedTarget) => {
    locationsToAdd.push(updatedTarget);
    locationsToDelete.push(updatedTarget);
  });

  return {
    locationsToAdd,
    locationsToDelete,
  };
};

export const generateIntentTargetValues = (intent, intentCriterion, campaignId) => {
  if (intent) {
    return [{
      Id: intentCriterion ? intentCriterion.Id : '',
      CriterionType: 'LocationIntentCriterion',
      Criterion: {
        Type: 'LocationIntentCriterion',
        LocationIntentCriterion: {
          IntentOption: intent,
        },
      },
      Campaign: {
        Id: campaignId,
      },
    }];
  }
  return [];
};

export const fromBingMapTargetToLocationTargetEDM = (locations, campaignId, entityActionType) => (
  _.map(locations, (location) => {
    let criterionType = null;
    if (location.isRadiusLocationTarget) {
      criterionType = 'Radius';
    } else if (location.targetingType === LOCATION_TARGET_TYPES.Included) {
      criterionType = 'Location';
    } else {
      criterionType = 'NegativeLocation';
    }

    const locationToSend = {
      Campaign: {
        Id: campaignId,
      },
      CriterionType: criterionType,
      SelectionStatus: location.targetingType === LOCATION_TARGET_TYPES.Included ?
        LOCATION_TARGET_TYPES.Included : LOCATION_TARGET_TYPES.Excluded,
      Status: entityActionType === ENTITY_ACTION_TYPES.Delete ? 'Deleted' : 'Active',
    };

    if (entityActionType === ENTITY_ACTION_TYPES.Update ||
      entityActionType === ENTITY_ACTION_TYPES.Delete) {
      locationToSend.Id = location.Id;
    }

    if (location.isRadiusLocationTarget) {
      locationToSend.Criterion = {
        Type: 'Radius',
        RadiusCriterion: {
          CountryCode: location.location.CountryCode,
          Latitude: location.location.GeoPoint.Latitude,
          Longitude: location.location.GeoPoint.Longitude,
          Name: location.location.ExpandedName,
          Radius: location.radiusSize,
          RadiusUnit: location.radiusUnit,
          LocationId: location.LocationId,
          Type: 'Radius',
        },
      };
    } else {
      locationToSend.Criterion = {
        Type: 'Location',
        LocationCriterion: {
          CountryCode: location.location.CountryCode,
          EnclosingLocationIds: [190, 4126],
          Latitude: location.location.GeoPoint.Latitude,
          Longitude: location.location.GeoPoint.Longitude,
          LesLocationType: location.location.Type,
          LocationStatus: 'Active',
          LocationType: location.location.Type,
          Name: location.location.ExpandedName,
          LocationId: location.LocationId,
          Type: 'Location',
        },
      };
    }
    return locationToSend;
  })
);
